/**
 * Personal Connected Services Component
 *
 * Provides user-level OAuth integrations with clear guidance about:
 * - What "personal" connections mean (your calendar, your meetings)
 * - Which services can be connected
 * - How these differ from organization-level settings
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Calendar,
  Briefcase,
  CheckSquare,
  Link,
  Unlink,
  Check,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Info,
  User,
  Building2,
  ShieldCheck,
  Mail,
  Users,
  ArrowLeftRight,
} from 'lucide-react';
import { oauthAPI, IntegrationStatuses, PROVIDER_INFO } from '@/services/api/oauth';
import { CloudFilesSettingsPanel } from '@/components/messaging/cloud/CloudFilesSettingsPanel';
import { CloudFilesRow, ConnectionDetails } from './ConnectionDetails';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppTranslation } from '@/contexts/TranslationContext';

type ProviderKey = 'zoom' | 'google' | 'microsoft' | 'asana';

const providerIcons: Record<ProviderKey | 'jitsi', React.ReactNode> = {
  zoom: <Video className="w-5 h-5" />,
  google: <Calendar className="w-5 h-5" />,
  microsoft: <Briefcase className="w-5 h-5" />,
  asana: <CheckSquare className="w-5 h-5" />,
  jitsi: <Video className="w-5 h-5" />,
};

interface IntegrationCardProps {
  provider: ProviderKey;
  status: IntegrationStatuses[ProviderKey];
  onConnect: (provider: ProviderKey) => void;
  onDisconnect: (provider: ProviderKey) => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  provider,
  status,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
}) => {
  const info = PROVIDER_INFO[provider];
  const isLoading = isConnecting || isDisconnecting;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`p-3 rounded-xl ${
              status.connected
                ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-600 dark:text-green-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
          >
            {providerIcons[provider]}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">{info.name}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">{info.description}</p>
          </div>
        </div>
        <div>
          {status.connected ? (
            <span className="flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-sm font-medium rounded-full">
              <Check className="w-4 h-4" />
              Connected
            </span>
          ) : (
            <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm rounded-full">
              <X className="w-4 h-4" />
              Not connected
            </span>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-wrap gap-2 mb-4">
        {info.features.map((feature) => (
          <span
            key={feature}
            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg"
          >
            {feature}
          </span>
        ))}
      </div>

      {/* Connected Account Info */}
      {status.connected && status.email && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <User className="w-4 h-4" />
          <span>Connected as: <span className="font-medium text-gray-900 dark:text-white">{status.email}</span></span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {status.connected ? (
          <>
            <button
              onClick={() => onDisconnect(provider)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 text-sm font-medium"
            >
              {isDisconnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
              Disconnect
            </button>
            <button
              onClick={() => onConnect(provider)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 border-2 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reconnect
            </button>
          </>
        ) : (
          <button
            onClick={() => onConnect(provider)}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 text-sm font-medium shadow-md"
          >
            {isConnecting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Link className="w-4 h-4" />
            )}
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

const GOOGLE_BASE = 'calendar,drive';
const GOOGLE_WITH_MAIL = 'calendar,drive,email';

export function PersonalConnectedServices() {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [connectingProvider, setConnectingProvider] = React.useState<ProviderKey | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = React.useState<ProviderKey | null>(null);

  // Fetch integration status
  const { data: status, isLoading, error, refetch } = useQuery({
    queryKey: ['integration-status'],
    queryFn: oauthAPI.getStatus,
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: async (provider: ProviderKey) => {
      setConnectingProvider(provider);
      let response;
      switch (provider) {
        case 'zoom':
          response = await oauthAPI.zoom.authorize();
          break;
        case 'google':
          // Calendar and Drive only. Gmail is a restricted scope with its own
          // consent (enableGoogleMailbox). A reconnect keeps it when it was
          // granted, or when the grant is unknown: those older connections all
          // asked for Gmail, and dropping it would silently stop their sync.
          response = await oauthAPI.google.authorize(
            status?.google?.connected && (status.google.capabilities?.mailbox ?? true) ? GOOGLE_WITH_MAIL : GOOGLE_BASE,
          );
          break;
        case 'microsoft':
          response = await oauthAPI.microsoft.authorize();
          break;
        case 'asana':
          response = await oauthAPI.asana.authorize();
          break;
      }
      window.location.href = response.auth_url;
    },
    onError: (error: any) => {
      setConnectingProvider(null);
      const detail = error?.response?.data?.detail || 'Failed to connect. Please try again.';
      alert(detail);
    },
  });

  const enableGoogleMailbox = useMutation({
    mutationFn: async () => {
      setConnectingProvider('google');
      const response = await oauthAPI.google.authorize(GOOGLE_WITH_MAIL);
      window.location.href = response.auth_url;
    },
    onError: (error: any) => {
      setConnectingProvider(null);
      alert(error?.response?.data?.detail || 'Failed to connect. Please try again.');
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async (provider: ProviderKey) => {
      setDisconnectingProvider(provider);
      switch (provider) {
        case 'zoom':
          return oauthAPI.zoom.disconnect();
        case 'google':
          return oauthAPI.google.disconnect();
        case 'microsoft':
          return oauthAPI.microsoft.disconnect();
        case 'asana':
          return oauthAPI.asana.disconnect();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-status'] });
      setDisconnectingProvider(null);
    },
    onError: () => {
      setDisconnectingProvider(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-gray-600 dark:text-gray-400">Failed to load integration status</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Guidance Banner - What are Personal Connections? */}
      <Card className="border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <User className="h-5 w-5" />
            {t('settings.your_personal_connections', 'Your Personal Connections')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-800 dark:text-blue-200 mb-4">
            {t('settings.personal_connections_description', 'These connections link YOUR personal accounts to the platform. When you connect a service:')}
          </p>
          <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
            <li className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>{t('settings.connections.bullet_calendar_title', 'Calendar')}</strong> {t('settings.connections.bullet_calendar', 'brings events from YOUR Google/Outlook calendar into WeaveIn')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>{t('settings.connections.bullet_files_title', 'Files')}</strong> {t('settings.connections.bullet_files', 'attach Google Drive / OneDrive & SharePoint files to messages (turned on per organization)')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>{t('settings.connections.bullet_mailbox_title', 'Mailbox')}</strong> {t('settings.connections.bullet_mailbox', 'read and send YOUR email from the Inbox → Mailbox tab')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Video className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>Meeting links</strong> create meetings in YOUR Zoom/Teams account</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>Task sync</strong> connects to YOUR Asana workspace</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Personal vs Org Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
          <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
            <User className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900 dark:text-green-100">Personal Connections (This Page)</h4>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
              Connect your personal accounts to sync your calendar and create meetings
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <Building2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Organization Settings</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Org admins can configure custom OAuth apps in Organization Settings
            </p>
          </div>
        </div>
      </div>

      {/* Video Conferencing Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Video className="w-5 h-5 text-blue-600" />
          Video Conferencing
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <IntegrationCard
            provider="zoom"
            status={status?.zoom || { connected: false }}
            onConnect={() => connectMutation.mutate('zoom')}
            onDisconnect={() => disconnectMutation.mutate('zoom')}
            isConnecting={connectingProvider === 'zoom'}
            isDisconnecting={disconnectingProvider === 'zoom'}
          />
          {/* Jitsi Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-600 dark:text-green-400">
                  {providerIcons.jitsi}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{PROVIDER_INFO.jitsi.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{PROVIDER_INFO.jitsi.description}</p>
                </div>
              </div>
              <span className="flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-sm font-medium rounded-full">
                <Check className="w-4 h-4" />
                Built-in
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {PROVIDER_INFO.jitsi.features.map((feature) => (
                <span
                  key={feature}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg"
                >
                  {feature}
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <ShieldCheck className="w-4 h-4 inline mr-1" />
              Jitsi Meet is always available. No account or setup required.
            </p>
          </div>
        </div>
      </div>

      {/* Calendar, Contacts & Email Sync Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-green-600" />
          {t('settings.connections.section_title', 'Email, Calendar & Files')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t(
            'settings.connections.section_hint',
            'Your Google and Microsoft accounts are yours: one connection serves every organization you work in. File access (Drive, OneDrive & SharePoint) is set up per organization.',
          )}
        </p>
        <div className="space-y-6">
          {/* Google Integration with Sync Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-xl ${
                    status?.google?.connected
                      ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-600 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Google Workspace</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Google Calendar, Meet, Drive & Gmail</p>
                </div>
              </div>
              <div>
                {status?.google?.connected ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-sm font-medium rounded-full">
                    <Check className="w-4 h-4" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm rounded-full">
                    <X className="w-4 h-4" />
                    Not connected
                  </span>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Gmail', 'Google Calendar', 'Google Meet', 'Google Drive'].map((feature) => (
                <span
                  key={feature}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg"
                >
                  {feature}
                </span>
              ))}
            </div>

            {/* Connected Account Info */}
            {status?.google?.connected && status?.google?.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <User className="w-4 h-4" />
                <span>Connected as: <span className="font-medium text-gray-900 dark:text-white">{status.google.email}</span></span>
              </div>
            )}

            {/* Connect/Disconnect Actions */}
            <div className="flex gap-2">
              {status?.google?.connected ? (
                <>
                  <button
                    onClick={() => disconnectMutation.mutate('google')}
                    disabled={disconnectingProvider === 'google'}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {disconnectingProvider === 'google' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Disconnect
                  </button>
                  <button
                    onClick={() => connectMutation.mutate('google')}
                    disabled={connectingProvider === 'google'}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => connectMutation.mutate('google')}
                  disabled={connectingProvider === 'google'}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 text-sm font-medium shadow-md"
                >
                  {connectingProvider === 'google' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4" />
                  )}
                  Connect Google Account
                </button>
              )}
            </div>

            {/* What the connection actually does (TRUS-638): granted scopes, real
                calendar sync, and this org's Drive / OneDrive & SharePoint. */}
            {status?.google?.connected && (
              <ConnectionDetails
                provider="google"
                capabilities={status.google.capabilities}
                onReconnect={() => connectMutation.mutate('google')}
                onEnableMailbox={() => enableGoogleMailbox.mutate()}
              />
            )}
            {!status?.google?.connected && (
              // Files are a separate, per-org sign-in: usable without this account.
              <ul className="mt-4">
                <CloudFilesRow provider="google_drive" />
              </ul>
            )}
          </div>

          {/* Microsoft/Outlook Integration with Sync Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-xl ${
                    status?.microsoft?.connected
                      ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 text-green-600 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Microsoft 365 / Outlook</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Outlook Calendar, Contacts & Email</p>
                </div>
              </div>
              <div>
                {status?.microsoft?.connected ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-sm font-medium rounded-full">
                    <Check className="w-4 h-4" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm rounded-full">
                    <X className="w-4 h-4" />
                    Not connected
                  </span>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Outlook Email', 'Outlook Calendar', 'Outlook Contacts', 'Microsoft Teams', 'OneDrive & SharePoint'].map((feature) => (
                <span
                  key={feature}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg"
                >
                  {feature}
                </span>
              ))}
            </div>

            {/* Connected Account Info */}
            {status?.microsoft?.connected && status?.microsoft?.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <User className="w-4 h-4" />
                <span>Connected as: <span className="font-medium text-gray-900 dark:text-white">{status.microsoft.email}</span></span>
              </div>
            )}

            {/* Connect/Disconnect Actions */}
            <div className="flex gap-2">
              {status?.microsoft?.connected ? (
                <>
                  <button
                    onClick={() => disconnectMutation.mutate('microsoft')}
                    disabled={disconnectingProvider === 'microsoft'}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {disconnectingProvider === 'microsoft' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Disconnect
                  </button>
                  <button
                    onClick={() => connectMutation.mutate('microsoft')}
                    disabled={connectingProvider === 'microsoft'}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => connectMutation.mutate('microsoft')}
                  disabled={connectingProvider === 'microsoft'}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 text-sm font-medium shadow-md"
                >
                  {connectingProvider === 'microsoft' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4" />
                  )}
                  Connect Microsoft Account
                </button>
              )}
            </div>

            {/* What the connection actually does (TRUS-638): granted scopes, real
                calendar sync, and this org's Drive / OneDrive & SharePoint. */}
            {status?.microsoft?.connected && (
              <ConnectionDetails
                provider="microsoft"
                capabilities={status.microsoft.capabilities}
                onReconnect={() => connectMutation.mutate('microsoft')}
              />
            )}
            {!status?.microsoft?.connected && (
              // Files are a separate, per-org sign-in: usable without this account.
              <ul className="mt-4">
                <CloudFilesRow provider="microsoft_files" />
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Task Management Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-purple-600" />
          Task Management
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <IntegrationCard
            provider="asana"
            status={status?.asana || { connected: false }}
            onConnect={() => connectMutation.mutate('asana')}
            onDisconnect={() => disconnectMutation.mutate('asana')}
            isConnecting={connectingProvider === 'asana'}
            isDisconnecting={disconnectingProvider === 'asana'}
          />
        </div>
      </div>

      {/* Privacy & Security Info */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-600" />
          Privacy & Security
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Your credentials are encrypted and stored securely</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>We only request permissions necessary for the features you use</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>You can disconnect at any time to revoke access</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Connections are specific to your account only</span>
          </li>
        </ul>
      </div>

      {/* Org policy for Drive / OneDrive & SharePoint (TRUS-638). Each user's own
          connection lives in the Google / Microsoft cards above. */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <CloudFilesSettingsPanel showUserConnections={false} />
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Status
        </button>
      </div>
    </div>
  );
}

export default PersonalConnectedServices;
